/**
 * The GUI's copy of the open project (name, file, saved revision, node positions) and the
 * project actions: save, save as, open, new, recent list.
 *
 * Positions are shared by everyone on the same core: `setPositions` (a whole layout, when it
 * settles) and `moveNodes` (a drag in progress) tell the core, which tells every page with
 * `project.nodes_moved` ({@link Project.onNodesMoved}).
 *
 * The class diagram's boxes are placed in the graph itself (`node.diagram`, so a move is an edit
 * with its undo); only a drag in progress goes through here ({@link Project.dragBoxes}), so
 * everyone sees it live.
 *
 * "Unsaved changes" is computed, never stored: the graph's revision differs from the revision
 * at the last save, or the nodes moved since the save of a project that has a file.
 */
import { createMemo, createSignal, type Accessor } from "solid-js";
import type { ProjectEntry } from "@protocol/ProjectEntry";
import type { ProjectState } from "@protocol/ProjectState";
import type { RecentProject } from "@protocol/RecentProject";
import type { Request } from "@protocol/Request";
import type { Response } from "@protocol/Response";
import { isClientError, type Client } from "../client/client";
import type { GraphMirror } from "../client/graph";
import { throttle } from "../shared/throttle";
import { toast } from "../shared/toast";
import type { CommandSet } from "../shared/commands";
import type { MenuSection, ToolbarButton } from "../shared/menus";

/** Node id → `[x, y]`. */
export type Positions = Record<string, [number, number]>;

/** How often a drag in progress is sent, at most (milliseconds). */
export const MOVE_MS = 50;

/** Nodes moved by someone (a `project.nodes_moved` event). */
export interface NodesMoved {
  /** Who moved them (the core's id for them). */
  by: string;
  /** Where they are now. */
  positions: Positions;
  /** A whole layout (`true`) or a drag in progress (`false`). */
  whole: boolean;
}

/** Diagram boxes someone is dragging (a `project.boxes_dragged` event). */
export interface BoxesDragged {
  /** Who drags them (the core's id for them). */
  by: string;
  /** Where they are now (top-left corners). */
  positions: Positions;
  /** The view they are dragged in (`board`; none: the class diagram and the blueprint). */
  view?: string | null;
  /** Their sizes while resized (`[w, h]` by id), on the board. */
  sizes?: Positions;
  /** The drag is over. */
  done: boolean;
}

/** One step of a drag this page sends. */
interface Dragged {
  /** Item id → top-left corner. */
  positions: Positions;
  /** The view dragged in (none: the diagram). */
  view?: string;
  /** Item id → size, while resizing on the board. */
  sizes?: Positions;
}

/** The projects folder's content and the recent list. */
export interface ProjectListing {
  folder: string | null;
  files: ProjectEntry[];
  recent: RecentProject[];
}

/** The mirrored project and its actions. */
export interface Project {
  /** The open project (`null` before the first answer). */
  state: Accessor<ProjectState | null>;
  /** `true` when there is something to save. */
  dirty: Accessor<boolean>;
  /** The last listing asked for. */
  listing: Accessor<ProjectListing | null>;
  /** Saves to the project's file (or a new one named after it). `name` renames first. */
  save(name?: string): Promise<boolean>;
  /** Saves to another file ("Save as"); a plain name goes in the projects folder. */
  saveAs(path: string, name?: string): Promise<boolean>;
  /** Opens a project file. */
  open(path: string): Promise<boolean>;
  /** Starts a new empty project. */
  create(name: string): Promise<boolean>;
  /** Reloads the projects folder and the recent list. */
  refresh(): Promise<void>;
  /** Removes an entry from the recent list. */
  forget(path: string): Promise<void>;
  /** Tells the core where the nodes are (sent at most a few times per second). */
  setPositions(positions: Positions): void;
  /** Tells the core some nodes are being dragged there (at most every {@link MOVE_MS}). */
  moveNodes(positions: Positions): void;
  /** Calls `handler` whenever someone's nodes moved (this page's own included). */
  onNodesMoved(handler: (moved: NodesMoved) => void): void;
  /**
   * Gives the project a way to read the current positions (the graph window does it), so a
   * save always stores where nodes are right now. The getter returns `null` when its
   * positions must not be sent (another project's positions not applied yet).
   */
  providePositions(getter: () => Positions | null): void;
  /**
   * Tells everyone where the diagram boxes this page drags are now (at most every
   * {@link MOVE_MS}); `done` ends the drag (sent at once, after the last move). `view` names
   * the view they are dragged in (`board`, which also sends their `sizes` while resizing).
   */
  dragBoxes(positions: Positions, done: boolean, view?: string, sizes?: Positions): void;
  /** Calls `handler` whenever someone drags diagram boxes (this page's own drags included). */
  onBoxesDragged(handler: (dragged: BoxesDragged) => void): void;
}

/** Positions with the project generation they were laid out for (none known yet: `undefined`, always taken). */
interface Stamped {
  /** Node id → `[x, y]`. */
  positions: Positions;
  /** `ProjectState.generation` when they were handed over. */
  generation: number | undefined;
}

/** Starts mirroring the project through `client`; `graph` tells whether the graph changed. */
export function createProject(client: Client, graph: GraphMirror): Project {
  const [state, setState] = createSignal<ProjectState | null>(null);
  const [listing, setListing] = createSignal<ProjectListing | null>(null);

  /**
   * The project these positions are laid out for, as this page knows it when they are handed
   * over: the core drops them if another project was opened since (an answer still on its way).
   */
  const generation = () => state()?.generation;

  /** Asks the core for the open project (on every (re)connection). */
  const load = async () => {
    try {
      const response = await client.request({ method: "project.info" });
      if (response.type === "project") setState(response.state);
    } catch {
      // Disconnected; the next onOpen loads it.
    }
  };
  client.onOpen(() => void load());
  /** Who wants to know about moved nodes. */
  const movedHandlers: ((moved: NodesMoved) => void)[] = [];
  /** The latest drag positions, sent at most every {@link MOVE_MS}. */
  const sendMoves = throttle<Stamped>(MOVE_MS, ({ positions, generation }) => {
    client.request({ method: "project.move_nodes", params: { positions, generation } }).catch(() => {});
  });
  /** Who wants to know about dragged boxes. */
  const draggedHandlers: ((dragged: BoxesDragged) => void)[] = [];
  /** The latest dragged boxes, sent at most every {@link MOVE_MS}; `null` ends the drag. */
  const sendDrag = throttle<Dragged | null>(MOVE_MS, (dragged) => {
    const params = dragged ? { positions: dragged.positions, done: false, view: dragged.view, sizes: dragged.sizes } : { positions: {}, done: true, view: lastView };
    client.request({ method: "project.drag_boxes", params }).catch(() => {});
  });
  /** The view of the drag in progress, said again when it ends. */
  let lastView: string | undefined;
  client.onEvent((event) => {
    if (event.event === "project.nodes_moved") for (const handler of movedHandlers) handler(event.data);
    if (event.event === "project.boxes_dragged") for (const handler of draggedHandlers) handler(event.data);
    if (event.event === "project.changed") {
      setState(event.data.state);
      if (listing()) void refresh();
    }
  });

  /** Sends a project request; shows the core's reason on failure. */
  const send = async (request: Request): Promise<Response | null> => {
    try {
      const response = await client.request(request);
      if (response.type === "project") setState(response.state);
      if (response.type === "project_list") setListing({ folder: response.folder, files: response.files, recent: response.recent });
      return response;
    } catch (error) {
      toast(isClientError(error) ? error.message : String(error), "error");
      return null;
    }
  };

  /** Reloads the projects folder and the recent list. */
  async function refresh(): Promise<void> {
    await send({ method: "project.list" });
  }

  /** Tells the user where the project was saved; `true` on success. */
  const savedTo = (response: Response | null) => {
    if (response?.type !== "project") return false;
    toast(`Saved to ${response.state.path ?? "?"}.`);
    return true;
  };

  /** The environment (and workspace) the page lays out: its positions replace only that board's. */
  const scope = () => {
    const shown = graph.scope?.();
    if (!shown?.id) return {};
    return { environment: shown.id, ...(shown.workspace ? { workspace: shown.workspace } : {}) };
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: Stamped | null = null;
  let current: (() => Positions | null) | null = null;

  /** Sends the latest positions now, before a save. Same connection, same module: in order. */
  const flush = () => {
    clearTimeout(timer);
    pending = null;
    const positions = current?.();
    if (positions) client.request({ method: "project.set_positions", params: { positions, ...scope(), generation: generation() } }).catch(() => {});
  };

  return {
    state,
    dirty: createMemo(() => {
      const s = state();
      if (!s || !graph.state.loaded) return false;
      return graph.state.revision !== s.saved_revision || (s.positions_changed && s.path !== null);
    }),
    listing,
    async save(name) {
      flush();
      return savedTo(await send({ method: "project.save", params: { path: null, name: name?.trim() || null } }));
    },
    async saveAs(path, name) {
      flush();
      return savedTo(await send({ method: "project.save", params: { path, name: name?.trim() || null } }));
    },
    async open(path) {
      const response = await send({ method: "project.open", params: { path } });
      if (response?.type !== "project") return false;
      toast(`Opened ${response.state.name}.`);
      return true;
    },
    async create(name) {
      const response = await send({ method: "project.new", params: { name } });
      return response?.type === "project";
    },
    refresh,
    async forget(path) {
      await send({ method: "project.forget_recent", params: { path } });
    },
    providePositions(getter) {
      current = getter;
    },
    moveNodes: (positions) => sendMoves({ positions, generation: generation() }),
    dragBoxes(positions, done, view, sizes) {
      lastView = view;
      // The throttle never holds `null` back behind a move: the end goes out after the last one.
      if (done) sendDrag(null);
      else sendDrag({ positions, view, sizes });
    },
    onBoxesDragged(handler) {
      draggedHandlers.push(handler);
    },
    onNodesMoved(handler) {
      movedHandlers.push(handler);
    },
    setPositions(positions) {
      pending = { positions, generation: generation() };
      clearTimeout(timer);
      timer = setTimeout(() => {
        const next = pending;
        pending = null;
        if (next) client.request({ method: "project.set_positions", params: { ...next, ...scope() } }).catch(() => {});
      }, 300);
    },
  };
}

/** The project's shortcut commands. */
export function projectCommands(project: Project): CommandSet {
  return { "project.save": () => void project.save() };
}

/** The project's File lines: save, and the Project window (new, open, save as, recent). */
export function projectMenus(project: Project): MenuSection[] {
  return [
    {
      menu: "file",
      section: "Project",
      order: 10,
      entries: () => [
        { label: "Save", command: "project.save", disabled: !project.dirty() },
        { label: "New, open or save as…", command: "window.open", argument: "project" },
      ],
    },
  ];
}

/** The toolbar's Save. */
export function projectToolbar(project: Project): ToolbarButton[] {
  return [{ id: "save", section: "project", order: 10, icon: "save", label: "Save", title: "Save the project", command: "project.save", disabled: () => !project.dirty() }];
}
